import { testIsNonEmptyString, testObjectHasKey } from '../../lib/test-and-assert/test-base';
import { globalLogger } from '../../qaamgo/helper/global-logger';
import { projectHelper } from '../misc/project-helper';
import { assertIsCurrency } from './assert';
import { assertIsNonEmptyString } from '../../lib/test-and-assert/assert-base';
import { satGlobals } from '../../qaamgo/sat/helper/sat-globals';

/**
 * @typedef {Object} PaymentData
 *
 * @property {'paypal'|'stripe'} [provider]
 * @property {?String} [product]
 * @property {'week'|'day'|'month'|'annual'} [billing]
 */

const FREE_TRIAL_SLUG_SUFFIX = '_free_trial';

export { FREE_TRIAL_SLUG_SUFFIX };

class SlugHelper {
    constructor() {
        /**
         * @type {Object<String, PricingDatum>}
         */
        this._slugData = {};
    }

    setSlugData(slugData) {
        this._slugData = slugData;

        // add all paypal slugs for easier access
        for (const [slug, data] of Object.entries(this._slugData)) {
            // pp_ prefix only needed for subscriptions
            if (!this.isSubscriptionSlug(slug)) {
                continue;
            }

            this._slugData['pp_' + slug] = data;
        }
    }

    /**
     * @param {String} slug
     *
     * @return {String}
     */
    getFreeTrialSlug(slug) {
        assertIsNonEmptyString(slug, 'invalid slug - slug is empty or no string');

        if (this.isFreeTrialSlug(slug)) {
            return slug;
        }

        return slug + FREE_TRIAL_SLUG_SUFFIX;
    }

    /**
     * @param {String} slug
     *
     * @return {Boolean}
     */
    isFreeTrialSlug(slug) {
        if (!testIsNonEmptyString(slug)) {
            return false;
        }

        return slug.endsWith(FREE_TRIAL_SLUG_SUFFIX);
    }

    /**
     * @param {String} slug
     *
     * @return {Boolean}
     */
    isSubscriptionSlug(slug) {
        if (!testIsNonEmptyString(slug)) {
            return false;
        }

        return slug.includes('month') || slug.includes('annual');
    }

    /**
     * @param {String} slug
     *
     * @returns {*|string}
     */
    getMonthlyCreditsFromSlug(slug) {
        return this._slugData[slug]?.monthly_credits ?? '';
    }

    /**
     * @param {PaymentData} paymentData
     * @param {'usd'|'eur'|'inr'} currency
     *
     * @returns {String}
     */
    getPriceString(paymentData, currency) {
        assertIsCurrency(currency);

        const slug = this.getSlug(paymentData, currency);

        return this.getPriceStringFromSlug(slug);
    }

    /**
     * @param {String} slug
     *
     * @returns {*|string}
     */
    getPriceStringFromSlug(slug) {
        return this._slugData[slug]?.price_formatted ?? '';
    }

    /**
     * @param {PaymentData} paymentData
     * @param {'usd'|'eur'|'inr'} currency
     *
     * @returns {PricingDatum}
     */
    getPriceData(paymentData, currency) {
        assertIsCurrency(currency);

        const slug = this.getSlug(paymentData, currency);

        return this.getDataForSlug(slug);
    }

    /**
     * @param {String} slug
     *
     * @returns {PricingDatum}
     */
    getDataForSlug(slug) {
        return this._slugData[slug] ?? {};
    }

    /**
     * @param {PaymentData} paymentData
     * @param {'usd'|'eur'|'inr'} currency
     */
    getMonthlyPriceForAnnual(paymentData, currency) {
        assertIsCurrency(currency);

        const slug = this.getSlug(paymentData, currency);

        return this.getMonthlyPriceForAnnualFromSlug(slug);
    }

    /**
     * @param {PaymentData} paymentData
     * @param {'usd'|'eur'} currency
     */
    getMonthlyPriceForAnnualSale(paymentData, currency) {
        assertIsCurrency(currency);

        const slug = this.getSlug(paymentData, currency);

        return this.getMonthlyPriceForAnnualSaleFromSlug(slug);
    }

    /**
     * @param {String} slug
     *
     * @returns {String}
     */
    getMonthlyPriceForAnnualFromSlug(slug) {
        return this._slugData[slug]?.price_monthly_formatted ?? '';
    }

    /**
     * @param {String} slug
     *
     * @returns {String}
     */
    getMonthlyPriceForAnnualSaleFromSlug(slug) {
        return this._slugData[slug]?.price_monthly_formatted ?? '';
    }

    /**
     * @param {PaymentData} paymentData
     * @param {'usd'|'eur'|'inr'} currency
     */
    getYearlyPriceForMonthly(paymentData, currency) {
        assertIsCurrency(currency);

        const slug = this.getSlug(paymentData, currency);

        return this.getYearlyPriceForMonthlyFromSlug(slug);
    }

    /**
     * @param {String} slug
     *
     * @returns {String}
     */
    getYearlyPriceForMonthlyFromSlug(slug) {
        return this._slugData[slug]?.price_yearly_formatted ?? '';
    }

    /**
     * @param {PaymentData} paymentData
     * @param {'usd'|'eur'|'inr'} currency
     */
    getYearlyAmountSavedForAnnual(paymentData, currency) {
        assertIsCurrency(currency);

        const slug = this.getSlug(paymentData, currency);

        return this.getYearlyAmountSavedForAnnualFromSlug(slug);
    }

    /**
     * @param {PaymentData} paymentData
     * @param {'usd'|'eur'} currency
     */
    getYearlyAmountSavedForAnnualSale(paymentData, currency) {
        assertIsCurrency(currency);

        const slug = this.getSlug(paymentData, currency);

        return this.getYearlySaleAmountSavedForAnnualFromSlug(slug);
    }

    /**
     * @param {String} slug
     *
     * @returns {String}
     */
    getYearlyAmountSavedForAnnualFromSlug(slug) {
        return this._slugData[slug]?.amount_saved_formatted ?? '';
    }

    /**
     * @param {String} slug
     *
     * @returns {String}
     */
    getYearlySaleAmountSavedForAnnualFromSlug(slug) {
        return this._slugData[slug]?.amount_saved_formatted ?? '';
    }

    /**
     * @param {PaymentData} paymentData
     * @param {'usd'|'eur'|'inr'} currency
     *
     * @returns {String}
     */
    getSlug(paymentData, currency) {
        assertIsCurrency(currency);

        const slug = this.#getProjectSlug(paymentData, currency);
        if (!this.isValidSlug(slug)) {
            globalLogger.log('slug helper - getSlug :: unknown slug', {
                info: JSON.stringify({
                    slug: slug,
                    paymentData: paymentData,
                    currency: currency,
                }),
            });

            return '';
        }

        return slug;
    }

    isValidSlug(slug) {
        return testObjectHasKey(this._slugData, slug);
    }

    /**
     * @param {PaymentData} paymentData
     * @param {'usd'|'eur'|'inr'} currency
     *
     * @returns {String}
     */
    #getProjectSlug(paymentData, currency) {
        assertIsCurrency(currency);

        if (!satGlobals.isCreditsInAllSatsEnabled() && projectHelper.isOnlineConvert()) {
            return this.#getSlugOc(paymentData, currency);
        }

        if (projectHelper.isApi2Convert()) {
            return this.#getSlugCustom('cm', paymentData, currency);
        }

        if (satGlobals.isCreditsEnabled() && paymentData.credits) {
            return this.#getSlugCustom('credits', paymentData, currency);
        }

        if (satGlobals.isCreditsInAllSatsEnabled()) {
            if (paymentData.product === 'subscription') {
                if (paymentData.provider === 'paypal') {
                    return this.#getSlugCustom('pp_sub', paymentData, currency);
                } else {
                    return this.#getSlugCustom('sub', paymentData, currency);
                }
            } else {
                return this.#getSlugCustom('payg', paymentData, currency);
            }
            
        }

        return this.#getSlugSat(paymentData, currency);
    }

    /**
     * @param {PaymentData} paymentData
     * @param {String} currency
     *
     * @returns {String}
     */
    #getSlugOc(paymentData, currency) {
        assertIsCurrency(currency);
        const version = '2';

        // slugs for 24h passes look like day_1000_eur
        if (paymentData.billing === 'day') {
            return `${paymentData.billing}_${paymentData.product}_${currency}_2`;
        }

        //slugs for sale subscriptions are following this pattern: [pp_]sub_[interval]_[currency]_[version]_sale_cyberweek
        if (paymentData.billing.includes('sale')) {
            let index = paymentData.billing.indexOf('annual');
            let slug = `${paymentData.billing.slice(0, index)}_${paymentData.product}_${paymentData.billing.slice(
                index,
                paymentData.billing.length
            )}`;

            slug = slug.replace('%currency%', currency).replace('%version%', version);

            slug = `sub${slug}`;

            if (paymentData.provider === 'paypal') {
                slug = 'pp_' + slug;
            }

            return slug;
        }

        // monthly/annual subscriptions look like sub_200_month_usd
        let slug = `sub_${paymentData.product}_${paymentData.billing}_${currency}_2`;

        // paypal slugs for subscriptions always have the pp_ prefix
        if (paymentData.provider === 'paypal') {
            slug = 'pp_' + slug;
        }

        return slug;
    }

    /**
     * @param {PaymentData} paymentData
     * @param {String} currency
     *
     * @returns {String}
     */
    #getSlugSat(paymentData, currency) {
        assertIsCurrency(currency);
        const version = '2';
        // slugs for weekly passes look like week_8000_usd
        if (paymentData.billing === 'week') {
            return `${paymentData.billing}_8000_${currency}`;
        }

        //slugs for sale subscriptions are following this pattern: [pp_]sub_[interval]_[currency]_[version]_sale_cyberweek
        if (paymentData.billing.includes('sale')) {
            let slug = paymentData.billing.replace('%currency%', currency).replace('%version%', version);
            slug = `sub_${slug}`;

            if (paymentData.provider === 'paypal') {
                slug = 'pp_' + slug;
            }

            return slug;
        }

        // monthly/annual subscriptions look like sub_month_usd
        let slug = `sub_${paymentData.billing}_${currency}_${version}`;

        //for starter, pro or ultimate subcriptions.
        if (paymentData.product === 'pro' || paymentData.product === 'ultimate' || paymentData.product === 'starter') {
            slug = `sub_${paymentData.product}_${paymentData.billing}_${currency}_${version}`;
        }

        // paypal slugs for subscriptions always have the pp_ prefix
        if (paymentData.provider === 'paypal') {
            slug = 'pp_' + slug;
        }

        return slug;
    }

    /**
     * @param {String} prefix 'cm' for Api2Convert & 'credits' for credits
     * @param {PaymentData} paymentData
     * @param {String} currency
     *
     * @returns {String}
     */
    #getSlugCustom(prefix, paymentData, currency) {
        assertIsCurrency(currency);

        if (satGlobals.isCreditsEnabled() && paymentData.credits) {
            return `${prefix}_${paymentData.credits}_${currency}`;
        }

        if (satGlobals.isCreditsInAllSatsEnabled()) {
            if (paymentData.product === 'subscription') {
                return `${prefix}_${paymentData.quota}_credits_${paymentData.billing}_${currency}`;
            } else {
                return `${prefix}_credits_${paymentData.quota}_${currency}`;
            } 
        }

        return `${prefix}_${paymentData.product}_${currency}`;
    }
}

export { SlugHelper };
